/**
 * Static content of every Spacetime insight (FTK-8209 epic). Copy comes from the "Content for insight
 * cards" tab of the design doc; what varies per space (status, pins, NEW) comes from the catalog.
 * Keys and order match SpacetimeCatalogService.InsightKeys on the server.
 */
export type SpacetimeInsightKey =
    | 'synopsis'
    | 'portfolio'
    | 'marketing'
    | 'audiences'
    | 'goals'
    | 'compare'
    | 'sfmc'
    | 'curacity'
    | 'ott'
    | 'rate-parity';

export interface SpacetimeInsightSpotlight {
    partner: string;
    name: string;
    role: string;
}

export interface SpacetimeInsightDefinition {
    key: SpacetimeInsightKey;
    /** Full name, shown on cards and the explainer. */
    title: string;
    /** Second line under the title on a card, such as the partner (mock-up: "By Aquiva", "VISTA"). */
    subtitle?: string;
    /** Short name, shown in the nav. */
    linkName: string;
    /** One line under the title on a card. */
    shortSummary: string;
    /** Explainer body. Paragraphs are separated by a blank line. */
    fullSummary: string;
    /** Explainer CTA labels without the trailing arrow. */
    ctaUnlocked: string;
    ctaLocked: string;
    /** Base insights are unlocked by default, vendor insights locked and shown in their own catalog section. */
    kind: 'base' | 'vendor';
    /** State of the insight itself, or null for one that has only a card and an explainer. */
    state: string | null;
    /** Explainer colours; undefined falls back to the template defaults (Flip.to green, Flip.to blue). */
    primaryColor?: string;
    accentColor1?: string;
    accentColor2?: string;
    /** "In this together with …" block on the explainer. */
    spotlight?: SpacetimeInsightSpotlight;
    /** Video spotlight on the explainer: a Cloudflare Stream slug once one exists, else a placeholder tile. */
    video?: { streamSlug?: string; title?: string; duration?: string };
}

export const SPACETIME_INSIGHTS: readonly SpacetimeInsightDefinition[] = [
    {
        key: 'synopsis',
        title: 'Synopsis',
        linkName: 'Synopsis',
        shortSummary: 'A clear read on the journey, and where to look first.',
        fullSummary:
            'See your whole audience move from visitors to planners to booked guests.\n\n' +
            'Where generic analytics see a session, Spacetime sees a traveler planning a trip and connects when ' +
            'they booked to the dates they’ll arrive, so every look at your business starts from how travel ' +
            'actually works. In that travel story, Synopsis is the plot.',
        ctaUnlocked: 'Read Synopsis',
        ctaLocked: 'Ask for access',
        kind: 'base',
        state: 'spacetime.synopsis',
    },
    {
        key: 'portfolio',
        title: 'Portfolio',
        linkName: 'Portfolio',
        shortSummary: 'See every property in one view and on the same scale.',
        fullSummary:
            'If Synopsis is the plot, Portfolio is the ensemble. Every property plays a part, and seeing them ' +
            'together (sized fairly, from the boutique to the resort) shows who’s carrying your group’s story ' +
            'and who’s ready for a bigger role.',
        ctaUnlocked: 'Go to Portfolio',
        ctaLocked: 'Ask for access',
        kind: 'base',
        state: 'spacetime.portfolio',
    },
    {
        key: 'marketing',
        title: 'Marketing',
        linkName: 'Marketing',
        shortSummary: 'See which channels turn into stays.',
        fullSummary:
            'Travel isn’t a straight line, so every channel and source gets credit for the role it actually ' +
            'plays (not just the last click). Marketing lets you back what’s working and dial in effort and ' +
            'spend where it might be needed.',
        ctaUnlocked: 'Go to Marketing',
        ctaLocked: 'Ask for access',
        kind: 'base',
        state: 'spacetime.marketing',
    },
    {
        key: 'audiences',
        title: 'Audiences',
        linkName: 'Audiences',
        shortSummary: 'Reach more travelers like the ones who already convert.',
        fullSummary:
            'Every audience brings something different to your business. The Audiences tab shows who’s ' +
            'planning, how they book and what each group contributes, so you can grow the ones that matter most.',
        ctaUnlocked: 'Meet your Audiences',
        ctaLocked: 'Ask for access',
        kind: 'base',
        state: 'spacetime.audiences',
    },
    {
        key: 'goals',
        title: 'Goals',
        linkName: 'Goals',
        shortSummary: 'Keep teams on pace, and know where to act.',
        fullSummary:
            'Track room nights and revenue against targets that keep teams, agencies and partners on pace. If ' +
            'something drifts, see it early enough to change course while there’s still time to close the gap.',
        ctaUnlocked: 'Set your Goals',
        ctaLocked: 'Ask for access',
        kind: 'base',
        state: 'spacetime.goals',
    },
    {
        key: 'compare',
        title: 'Compare',
        linkName: 'Compare',
        shortSummary: 'Put any two properties, marketing segments or time periods side by side.',
        fullSummary:
            'Get a plain read on what’s behind the difference, led by a one-sentence takeaway built on fixed, ' +
            'verifiable numbers. No analyst or export needed (so you know what to change, not just what changed).',
        ctaUnlocked: 'Try Compare',
        ctaLocked: 'Ask for access',
        kind: 'base',
        state: 'spacetime.compare',
    },
    {
        key: 'sfmc',
        title: 'Salesforce Marketing Cloud',
        subtitle: 'By Aquiva',
        linkName: 'SFMC',
        shortSummary: 'See where sends turn into stays.',
        fullSummary:
            'Measure reach and opens from Salesforce Marketing Cloud alongside behavioral analytics to see what ' +
            'happened next: who planned, who booked and what each send was worth.',
        ctaUnlocked: 'See the impact',
        ctaLocked: 'Get set up',
        kind: 'vendor',
        state: 'spacetime.sfmc',
        spotlight: { partner: 'Aquiva', name: 'Bob Magliozzi', role: 'VP, Business Development' },
    },
    {
        key: 'curacity',
        title: 'Curacity',
        subtitle: 'VISTA',
        linkName: 'Curacity',
        shortSummary: 'See the impact of VISTA’s distributed brand presence.',
        fullSummary:
            'Curacity puts your brand in front of entirely new audiences. Now you can watch them become guests. ' +
            'Spacetime follows every net-new visitor from discovery through planning to booking, so the audience ' +
            'you’re growing is clear to see.',
        ctaUnlocked: 'See the impact',
        ctaLocked: 'Get introduced to Curacity',
        kind: 'vendor',
        state: 'spacetime.curacity',
        spotlight: { partner: 'Curacity', name: 'Brian Gauthier', role: 'VP, Product Management' },
    },
    {
        key: 'ott',
        title: 'OTT',
        subtitle: 'by Awestruck',
        linkName: 'OTT',
        shortSummary: 'See how connected TV turns viewers into guests.',
        fullSummary:
            'Your story belongs on the biggest screen in the house. With Awestruck, you can follow viewers from ' +
            'the living room to your site and into a stay, so connected TV (OTT) earns its place in the plan with ' +
            'real results.',
        ctaUnlocked: 'See the impact',
        ctaLocked: 'Get introduced to Awestruck',
        kind: 'vendor',
        state: 'spacetime.ott',
        spotlight: { partner: 'Awestruck', name: 'Tom Smith', role: 'VP, Business Development' },
    },
    {
        key: 'rate-parity',
        title: 'Rate Parity',
        linkName: 'Rate Parity',
        shortSummary: 'Win on rate, on the dates that matter.',
        fullSummary:
            'Keep your direct rate winning, and know the dates worth fighting for. Amadeus rate data meets ' +
            'Spacetime demand signals on Rate Parity, so your team sees where parity matters most (and what to ' +
            'do next).',
        ctaUnlocked: 'See my Parity',
        ctaLocked: 'Ask for access',
        kind: 'vendor',
        // Card and explainer only: the insight itself has no Spacetime view yet.
        state: null,
        spotlight: { partner: 'Amadeus Advertising', name: 'Pamela Borja', role: 'Metasearch, Geo, & Reporting' },
    },
];

const BY_KEY = new Map<string, SpacetimeInsightDefinition>(SPACETIME_INSIGHTS.map(insight => [insight.key, insight]));

/** The definition of a catalog key, or undefined for a key this build does not know. */
export function spacetimeInsightDefinition(key: string): SpacetimeInsightDefinition | undefined {
    return BY_KEY.get(key);
}
